import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  AppBar,
  Typography,
  Button,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  PeopleAltOutlined,
  TimelineOutlined,
  BusinessOutlined,
  RequestQuoteOutlined,
  ReceiptLongOutlined,
  MenuBookOutlined,
  PersonOutline,
  PhoneInTalkOutlined,
  LogoutOutlined,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { SALES } from '../components/sales/salesUi';
import { BrandLogo } from '../branding/BrandLogo';
import APP_VERSION from '../version';

const drawerWidth = 240;

const NAV_ITEMS = [
  { label: 'My Leads', path: '/sales/leads', icon: <PeopleAltOutlined fontSize="small" /> },
  { label: 'Activity Log', path: '/sales/activity', icon: <TimelineOutlined fontSize="small" /> },
  { label: 'Customers', path: '/sales/customers', icon: <BusinessOutlined fontSize="small" /> },
  { label: 'Quote Requests', path: '/sales/quote-requests', icon: <RequestQuoteOutlined fontSize="small" /> },
  { label: 'Expenses', path: '/sales/expenses', icon: <ReceiptLongOutlined fontSize="small" /> },
  { label: 'Guide', path: '/sales/guide', icon: <MenuBookOutlined fontSize="small" /> },
  { label: 'Profile', path: '/sales/profile', icon: <PersonOutline fontSize="small" /> },
];

const SalesPortalLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const displayName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Sales Rep';
  const [navOpen, setNavOpen] = useState(true);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: SALES.surface }}>
      <AppBar
        position="fixed"
        className="no-print"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: SALES.white,
          color: SALES.ink,
          borderBottom: `1px solid ${SALES.line}`,
        }}
      >
        <Toolbar sx={{ minHeight: 56, gap: 1.5, px: { xs: 1.5, md: 3 } }}>
          <IconButton
            color="inherit"
            aria-label={navOpen ? 'Hide navigation' : 'Show navigation'}
            edge="start"
            size="small"
            onClick={() => setNavOpen((open) => !open)}
            sx={{ color: SALES.ink }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexGrow: 1 }}>
            <Box
              component="img"
              src="/fabworks-logo-color.jpg"
              alt="Fab Works"
              sx={{ height: 28, width: 'auto', borderRadius: 0.5 }}
            />
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.1, color: SALES.navy }}>
                Fab Works
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: SALES.muted, lineHeight: 1.1 }}>
                Outside Sales Portal
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: SALES.steelSoft,
                color: SALES.steel,
                fontSize: '0.8rem',
                fontWeight: 700,
              }}
            >
              {(user?.first_name?.[0] || 'S').toUpperCase()}
            </Avatar>
            <Box sx={{ display: { xs: 'none', md: 'block' }, mr: 0.5 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.1 }}>
                {displayName}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: SALES.muted, lineHeight: 1.1 }}>
                Outside Sales Rep
              </Typography>
            </Box>
            <Button
              color="inherit"
              size="small"
              startIcon={<LogoutOutlined />}
              onClick={() => logout()}
              sx={{ color: SALES.muted, textTransform: 'none', fontWeight: 600 }}
            >
              Log out
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="persistent"
        anchor="left"
        open={navOpen}
        className="no-print"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          display: navOpen ? 'block' : 'none',
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: '56px',
            height: 'calc(100vh - 56px)',
            position: 'fixed',
            borderRight: 'none',
            bgcolor: SALES.navy,
            color: '#D7E3EF',
            backgroundImage:
              'linear-gradient(180deg, rgba(27,108,168,0.18) 0%, transparent 28%), radial-gradient(circle at top left, rgba(255,255,255,0.06), transparent 40%)',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <Box sx={{ px: 1.75, pt: 2.5 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<PhoneInTalkOutlined fontSize="small" />}
              onClick={() => navigate('/sales/activity/new')}
              sx={{
                bgcolor: SALES.steel,
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
                py: 1,
                '&:hover': { bgcolor: '#2380C4', boxShadow: 'none' },
              }}
            >
              Log Call / Visit
            </Button>
          </Box>
          <Box sx={{ px: 2, pt: 2.5, pb: 1.5 }}>
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
                color: 'rgba(215,227,239,0.55)',
              }}
            >
              Workspace
            </Typography>
          </Box>
          <List dense sx={{ px: 1.25, flex: 1, overflow: 'auto' }}>
            {NAV_ITEMS.map((item) => {
              const selected =
                location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
              return (
                <ListItemButton
                  key={item.path}
                  selected={selected}
                  onClick={() => navigate(item.path)}
                  sx={{
                    mb: 0.5,
                    borderRadius: 1.5,
                    color: selected ? '#FFFFFF' : 'rgba(215,227,239,0.82)',
                    bgcolor: selected ? 'rgba(27,108,168,0.45)' : 'transparent',
                    border: selected ? '1px solid rgba(120,180,220,0.35)' : '1px solid transparent',
                    '&:hover': {
                      bgcolor: selected ? 'rgba(27,108,168,0.55)' : 'rgba(255,255,255,0.06)',
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: 'rgba(27,108,168,0.55)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: selected ? 700 : 500 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
          <Box
            onClick={() => navigate('/sales/leads')}
            sx={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
              px: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.85 },
            }}
          >
            <BrandLogo
              slot="sidebar"
              sx={{ width: '100%', justifyContent: 'center' }}
              imgSx={{
                maxWidth: '90%',
                width: '100%',
                height: 'auto',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                color: 'rgba(215,227,239,0.55)',
                fontSize: '0.7rem',
              }}
            >
              v{APP_VERSION}
            </Typography>
          </Box>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          mt: '56px',
          maxWidth: 1200,
          minHeight: 'calc(100vh - 56px)',
          '@media print': {
            mt: 0,
            p: 0,
            maxWidth: '100%',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default SalesPortalLayout;
